import React, { useState } from 'react';
import Comment from './Comment';
import { ListSkeleton } from './Skeletons';
import UnifiedCommentDto from '../../types/comment.types';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns'; // Import date-fns
import CommentThreadPopup from './CommentThreadPopup';

interface CommentsTableProps {
  comments: UnifiedCommentDto[];
  selectedComment: number | null;
  // eslint-disable-next-line no-unused-vars
  setSelectedComment: (index: number | null) => void;
  isLoading: boolean;
  totalComments: number;
  // eslint-disable-next-line no-unused-vars
  clickEvent: (index: number) => void;
  activePlatform?: any;
  commentsPerPage?: number; // <-- Add this
}

const CommentsTable: React.FC<CommentsTableProps> = ({
  comments,
  selectedComment,
  setSelectedComment,
  isLoading,
  totalComments,
  clickEvent,
  activePlatform,
  commentsPerPage = 10, // <-- Default fallback
}) => {
  const [threadPopup, setThreadPopup] = useState<{
    isOpen: boolean;
    platformCommentId: string;
    originalComment: UnifiedCommentDto | null;
  }>({
    isOpen: false,
    platformCommentId: '',
    originalComment: null,
  });

  // Generate the appropriate settings link based on active platform
  const settingsLink = activePlatform
    ? `/settings/${activePlatform.provider}`
    : '/settings';

  const navigate = useNavigate();
  const handleSettingsClick = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    navigate(settingsLink);

    setTimeout(() => {
      const automodElement = document.getElementById('automod');
      if (automodElement) {
        automodElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleOpenThread = (platformCommentId: string) => {
    const comment = comments.find(
      (c) => c.platformCommentId === platformCommentId
    );
    if (comment) {
      setThreadPopup({
        isOpen: true,
        platformCommentId,
        originalComment: comment,
      });
    }
  };

  // Handle closing thread popup
  const handleCloseThread = () => {
    setThreadPopup({
      isOpen: false,
      platformCommentId: '',
      originalComment: null,
    });
  };

  return (
    <div className="[159px]-h">
      {totalComments === 0 && !isLoading ? (
        <div className="pl-4 mt-4">
          <p>Comments come in as they are sent!</p>
          <br />
          {location.pathname === '/autoMod' ? (
            <p>
              Go to{' '}
              <a
                href="#"
                onClick={handleSettingsClick}
                className="text-blue-500 hover:underline"
              >
                Settings
              </a>{' '}
              to configure automod for your comments.
            </p>
          ) : (
            <p>No comments yet!</p>
          )}
        </div>
      ) : (
        <table className="table-fixed border-collapse w-full border-y border-white border-opacity-20 mt-1.5">
          <thead className="text-left pb-2 commentTableHead">
            <tr>
              <th className="text-center w-12 text-xs"></th>
              <th className="w-12 text-xs">Post</th>
              <th className="pl-4 w-40 text-xs">User</th>
              <th className="text-xs">Comment</th>
              <th className="w-24 text-xs">Thread</th>
              <th className="w-24 text-xs">Time</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <ListSkeleton rows={commentsPerPage} />
            ) : (
              comments.map((comment, index) => (
                <Comment
                  status={comment.status}
                  username={comment.username}
                  comment={comment.message}
                  key={comment.platformCommentId}
                  index={index}
                  clickEvent={clickEvent}
                  selectedComment={selectedComment ?? -1}
                  setSelectedComment={setSelectedComment}
                  timestamp={formatDistanceToNow(
                    new Date(comment.platformCreatedAt),
                    { addSuffix: true }
                  ).replace('about ', '')}
                  attachment={comment.attachmentUrl || ''}
                  post={comment.post || ''}
                  autoFlagged={comment.moderationStatus}
                  isHidden={comment.hidden}
                  replyCount={comment.replyCount}
                  platformCommentId={comment.platformCommentId}
                  onOpenThread={handleOpenThread}
                />
              ))
            )}
          </tbody>
        </table>
      )}

      {/* Thread Popup */}
      {threadPopup.originalComment && (
        <CommentThreadPopup
          isOpen={threadPopup.isOpen}
          onClose={handleCloseThread}
          platformCommentId={threadPopup.platformCommentId}
          originalComment={threadPopup.originalComment}
        />
      )}
    </div>
  );
};

export default CommentsTable;
